import { useEffect, useState } from 'react';
import {
  DEFAULT_DISTANCE_UNIT,
  formatCalculatedDistanceText,
  formatDistance,
} from '../../shared/distance';
import type { AccountAnimalToday, AccountAnimalTrack, DistanceUnit } from '../../shared/types/api';
import { CompassMark } from '../components/CompassMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { LoadingTrail } from '../components/LoadingTrail';
import { TrailCompassArtwork } from '../components/TrailCompassArtwork';
import { AccountLearningJourney } from '../features/learning/AccountLearningPanel';
import { TrackMap } from '../features/map/TrackMap';
import { ApiClientError, api } from '../lib/api';
import { SIMULATED_TRAIL_DISCLOSURE } from '../lib/simulatedTrailDisclosure';

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : 'This trail could not load.';
}

function formatDate(timestamp: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(timestamp));
}

export function AnimalAdventurePage({
  entitlementId,
  onBack,
  onOpenGrownupArea,
}: {
  entitlementId: string;
  onBack: () => void;
  onOpenGrownupArea: () => void;
}) {
  const [track, setTrack] = useState<AccountAnimalTrack | null>(null);
  const [today, setToday] = useState<AccountAnimalToday | null>(null);
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(DEFAULT_DISTANCE_UNIT);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [learningBusy, setLearningBusy] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadAdventure() {
      try {
        const [account, loadedTrack] = await Promise.all([
          api.getAccount(),
          api.getAccountAnimalTrack(entitlementId, 15),
        ]);
        if (!active) return;
        setDistanceUnit(account.distanceUnit);
        setTrack(loadedTrack);
        if (loadedTrack.status === 'ready') {
          try {
            const loadedToday = await api.getAccountAnimalToday(entitlementId);
            if (active) setToday(loadedToday);
          } catch (reason) {
            if (!active) return;
            if (reason instanceof ApiClientError && reason.status === 401) {
              onOpenGrownupArea();
              return;
            }
            setError(messageFrom(reason));
          }
        }
      } catch (reason) {
        if (!active) return;
        if (reason instanceof ApiClientError && reason.status === 401) {
          onOpenGrownupArea();
          return;
        }
        setError(messageFrom(reason));
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadAdventure();
    return () => {
      active = false;
    };
  }, [entitlementId, onOpenGrownupArea]);

  async function retryLearning() {
    setLearningBusy(true);
    try {
      const loadedToday = await api.getAccountAnimalToday(entitlementId);
      setToday(loadedToday);
      setError(null);
    } catch (reason) {
      setToday(null);
      if (reason instanceof ApiClientError && reason.status === 401) {
        onOpenGrownupArea();
        return;
      }
      setError(messageFrom(reason));
    } finally {
      setLearningBusy(false);
    }
  }

  if (loading) return <LoadingTrail message="Drawing the trail map…" />;
  if (!track) {
    return (
      <div className="app-shell tracking-shell">
        <AdventureTopbar onBack={onBack} onOpenGrownupArea={onOpenGrownupArea} />
        <main>{error && <ErrorNotice message={error} />}</main>
      </div>
    );
  }
  return (
    <AnimalAdventure
      entitlementId={entitlementId}
      track={track}
      today={today}
      distanceUnit={distanceUnit}
      error={error}
      onBack={onBack}
      onOpenGrownupArea={onOpenGrownupArea}
      onRetryLearning={() => void retryLearning()}
      learningBusy={learningBusy}
    />
  );
}

function AdventureTopbar({
  onBack,
  onOpenGrownupArea,
}: {
  onBack: () => void;
  onOpenGrownupArea: () => void;
}) {
  return (
    <header className="tracker-topbar">
      <button className="back-button" type="button" onClick={onBack}>
        <span aria-hidden="true">←</span> Trail book
      </button>
      <a className="brand compact-brand" href="/" aria-label="Wild Trails Kids home">
        <CompassMark />
        <span>Wild Trails Kids</span>
      </a>
      <button type="button" className="parent-link" onClick={onOpenGrownupArea}>
        Grown-up area
      </button>
    </header>
  );
}

export function AnimalAdventure({
  entitlementId,
  track,
  today,
  distanceUnit = DEFAULT_DISTANCE_UNIT,
  error,
  onBack,
  onOpenGrownupArea,
  onRetryLearning,
  learningBusy = false,
  initialHistoryExpanded = false,
}: {
  entitlementId: string;
  track: AccountAnimalTrack;
  today: AccountAnimalToday | null;
  distanceUnit?: DistanceUnit;
  error: string | null;
  onBack: () => void;
  onOpenGrownupArea: () => void;
  onRetryLearning?: () => void;
  learningBusy?: boolean;
  initialHistoryExpanded?: boolean;
}) {
  const [centerSignal, setCenterSignal] = useState(0);
  const [historyExpanded, setHistoryExpanded] = useState(initialHistoryExpanded);
  const latestPoint = track.status === 'ready' ? track.points.at(-1) : null;
  const recentDays = track.status === 'ready' ? [...track.days].reverse() : [];
  const visibleDays = historyExpanded ? recentDays : recentDays.slice(0, 5);

  return (
    <div className="app-shell tracking-shell adventure-shell">
      <AdventureTopbar onBack={onBack} onOpenGrownupArea={onOpenGrownupArea} />
      <main>
        {error && <ErrorNotice message={error} />}
        <section className="animal-heading adventure-animal-heading">
          <img src={track.animal.portraitUrl} alt={`${track.animal.name} portrait`} />
          <div>
            <p className="eyebrow">Today’s adventure</p>
            <h1>{track.animal.name}</h1>
            <p>{track.animal.speciesName}</p>
          </div>
        </section>

        {track.status === 'preparing' ? (
          <section className="adventure-preparing" role="status">
            <TrailCompassArtwork compact />
            <div>
              <p className="eyebrow">Adventure in progress</p>
              <h2>{track.animal.name}’s first trail is getting ready.</h2>
              <p>
                Your animal friend is safely in the trail book. Come back soon to see the first
                simulated journey appear on the map. {SIMULATED_TRAIL_DISCLOSURE}
              </p>
              <button type="button" onClick={onBack}>
                Choose another animal
              </button>
            </div>
          </section>
        ) : (
          <>
            <section
              className="map-section adventure-map-section"
              aria-label={`${track.animal.name}'s simulated trail map`}
            >
              <div className="adventure-map-heading">
                <div>
                  <p className="eyebrow">Today’s trail map</p>
                  <h2>Follow {track.animal.name}’s latest route</h2>
                </div>
                <span>Simulated trail</span>
              </div>
              <TrackMap points={track.points} centerSignal={centerSignal} />
              <div className="map-actions">
                <button type="button" onClick={() => setCenterSignal((value) => value + 1)}>
                  <span aria-hidden="true">⌖</span> Center on {track.animal.name}
                </button>
              </div>
              {latestPoint && (
                <p className="map-text-alternative">
                  Latest point on the simulated trail: latitude {latestPoint.latitude.toFixed(2)},
                  longitude {latestPoint.longitude.toFixed(2)}. {SIMULATED_TRAIL_DISCLOSURE}
                </p>
              )}
            </section>

            <section
              className="tracking-details adventure-details"
              aria-labelledby="trail-details-title"
            >
              <div className="adventure-details-heading">
                <p className="eyebrow">Trail notes</p>
                <h2 id="trail-details-title">What happened along the way</h2>
              </div>
              <div className="stats-grid">
                <article>
                  <span className="stat-icon" aria-hidden="true">
                    ◷
                  </span>
                  <p>Last update</p>
                  <strong>{formatDate(track.lastUpdate)}</strong>
                </article>
                <article>
                  <span className="stat-icon" aria-hidden="true">
                    ⌁
                  </span>
                  <p>Recent distance</p>
                  <strong>{formatDistance(track.totalDistanceKm, distanceUnit)}</strong>
                </article>
              </div>

              <section className="history-panel" aria-labelledby="trail-history-title">
                <div className="history-heading">
                  <div>
                    <p className="eyebrow">Trail history</p>
                    <h2 id="trail-history-title">
                      {historyExpanded ? 'All' : 'Latest'} {visibleDays.length}{' '}
                      {visibleDays.length === 1 ? 'day' : 'days'}
                    </h2>
                  </div>
                  <span>{track.points.length} points</span>
                </div>
                <ol id="trail-history-days">
                  {visibleDays.map((day, index) => (
                    <li key={day.date}>
                      <span className={index === 0 ? 'history-dot latest' : 'history-dot'} />
                      <div>
                        <time dateTime={day.date}>{formatDate(`${day.date}T12:00:00.000Z`)}</time>
                        <p>
                          {formatCalculatedDistanceText(
                            day.shortNarrative,
                            day.dailyDistanceKm,
                            distanceUnit,
                          )}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
                {recentDays.length > 5 && (
                  <button
                    type="button"
                    className="history-toggle"
                    aria-expanded={historyExpanded}
                    aria-controls="trail-history-days"
                    onClick={() => setHistoryExpanded((expanded) => !expanded)}
                  >
                    {historyExpanded
                      ? 'Show 5 recent days'
                      : `Show all ${recentDays.length} trail days`}
                  </button>
                )}
              </section>
            </section>

            {today?.status === 'ready' && (
              <section className="adventure-learning" aria-labelledby="daily-discovery-title">
                <div className="adventure-section-heading">
                  <p className="eyebrow">Daily discovery · {today.date}</p>
                  <h2 id="daily-discovery-title">Learn with today’s trail</h2>
                </div>
                <AccountLearningJourney
                  today={today}
                  entitlementId={entitlementId}
                  animalName={track.animal.name}
                  animalSpeciesName={track.animal.speciesName}
                />
              </section>
            )}
            {today?.status === 'preparing' && (
              <section
                className="adventure-learning adventure-learning-pending"
                aria-labelledby="daily-discovery-title"
                role="status"
              >
                <p className="eyebrow">Daily discovery · {today.date}</p>
                <h2 id="daily-discovery-title">Today’s learning is getting ready.</h2>
                <p>
                  Your trail is ready to explore. Check back in a few moments for today’s fact,
                  story, quiz, and activity.
                </p>
                <button type="button" onClick={onRetryLearning} disabled={learningBusy}>
                  {learningBusy ? 'Checking for updates…' : 'Refresh learning status'}
                </button>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
